export type StatsMessageRole = 'user' | 'assistant' | 'system_error'

export type StatsMessageType = 'text' | 'data' | 'chart' | 'error'

// Spec Vega-Lite brute renvoyée par le backend, re-rendable telle quelle par
// vega-embed. Opaque côté front : on ne la manipule jamais.
export type VegaSpec = Record<string, unknown>

// Trace de la requête Looker générée (audit) — opaque côté front.
export type LookerQueryTrace = Record<string, unknown>

export interface StatsDataColumn {
  name: string
  label: string
}

export type StatsDataCellValue = string | number | boolean | null

export interface TextMessageContent {
  text: string
  isFinal: boolean
}

export interface DataMessageContent {
  columns: StatsDataColumn[]
  rows: StatsDataCellValue[][]
  truncated: boolean
  queryTrace: LookerQueryTrace
}

export interface ChartMessageContent {
  vegaSpec: VegaSpec
  title: string
}

export interface ErrorMessageContent {
  code: string
  userMessage: string
}

interface StatsMessageBase {
  ulid: string
  role: StatsMessageRole
  createdAt: string
}

export interface StatsTextMessage extends StatsMessageBase {
  type: 'text'
  content: TextMessageContent
}

export interface StatsDataMessage extends StatsMessageBase {
  type: 'data'
  content: DataMessageContent
}

export interface StatsChartMessage extends StatsMessageBase {
  type: 'chart'
  content: ChartMessageContent
}

export interface StatsErrorMessage extends StatsMessageBase {
  type: 'error'
  content: ErrorMessageContent
}

export type StatsMessage =
  | StatsTextMessage
  | StatsDataMessage
  | StatsChartMessage
  | StatsErrorMessage

export interface StatsConversationSummary {
  ulid: string
  title: string | null
  updatedAt: string
  lastMessagePreview: string | null
  agentProfile: string
  isProcessing: boolean
}

export interface StatsConversationList {
  items: StatsConversationSummary[]
  total: number
}

export interface StatsConversationDetail {
  ulid: string
  title: string | null
  agentProfile: string
  /** Un tour est en cours de traitement par le consumer (réponse attendue via Centrifugo). */
  isProcessing: boolean
  messages: StatsMessage[]
}

export interface CreatedStatsConversation {
  ulid: string
  title: string | null
  agentProfile: string
}

export interface StatsAgentProfile {
  key: string
  label: string
  description: string
  suggestions: string[]
}

export interface StatsAgentProfileList {
  defaultProfile: string
  items: StatsAgentProfile[]
}

export interface SendMessagePayload {
  message: string
  replaceFromMessageUlid?: string
}

export interface SendMessageResponse {
  userMessage: StatsMessage
}

// Events du channel Centrifugo stats_assistant_{userUuid}

export type StatsTurnStatus = 'completed' | 'cancelled' | 'failed'

export interface StatsTurnDonePayload {
  status: StatsTurnStatus
  title: string | null
  /** Erreur non persistée (bug côté back) : les erreurs Looker arrivent en message. */
  error: ErrorMessageContent | null
}

export type StatsAssistantEvent = { conversationUlid: string } & (
  | { event: 'progress'; data: { text: string } }
  | { event: 'message'; data: StatsMessage }
  | { event: 'done'; data: StatsTurnDonePayload }
)

// Erreur HTTP du module (409 TURN_IN_PROGRESS, 429 QUOTA_EXCEEDED) : userMessage affiché tel quel.
export class StatsAssistantRequestError extends Error {
  constructor(
    public status: number,
    public code: string | null,
    public userMessage: string | null,
  ) {
    super(userMessage ?? `Erreur ${status} de l'assistant statistiques`)
  }
}

export const TURN_IN_PROGRESS_CODE = 'TURN_IN_PROGRESS'

/** Profil par défaut tant que GET /stats_assistant/agents n'a pas répondu. */
export const DEFAULT_AGENT_PROFILE = 'generaliste'

export interface ConversationTurnState {
  isProcessing: boolean
  progressText: string | null
  error: string | null
  /** Envoi refusé par le back (le message n'existe pas côté serveur) : rejoué tel quel par « Réessayer ». */
  failedRequest: SendMessagePayload | null
}

/** Message user affiché avant la réponse du back, remplacé par celui renvoyé par le POST. */
export const OPTIMISTIC_MESSAGE_ULID_PREFIX = 'optimistic-user-'

export const isOptimisticMessage = (message: Pick<StatsMessage, 'ulid'>): boolean =>
  message.ulid.startsWith(OPTIMISTIC_MESSAGE_ULID_PREFIX)
